import React, { useEffect, useMemo } from 'react';
import { SITE_URL } from './MetaTags';
import './Breadcrumbs.css';

const toAbsoluteUrl = (href) => {
  if (!href) return undefined;
  return /^https?:\/\//i.test(href)
    ? href
    : `${SITE_URL}${href.startsWith('/') ? href : `/${href}`}`;
};

/**
 * Visible, semantic breadcrumbs shared by public landing, article, and tool
 * pages. The same route model is emitted as BreadcrumbList JSON-LD so the
 * prerenderer captures it for crawlers and social previews.
 */
const Breadcrumbs = ({ items = [] }) => {
  const normalizedItems = useMemo(
    () => items.filter((item) => item && item.label).map((item) => ({
      label: item.label,
      href: item.href
    })),
    [items]
  );

  useEffect(() => {
    if (!normalizedItems.length) return undefined;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: normalizedItems.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.label,
        ...(item.href ? { item: toAbsoluteUrl(item.href) } : {})
      }))
    };

    const tag = document.createElement('script');
    tag.type = 'application/ld+json';
    tag.setAttribute('data-ivt-breadcrumb-schema', 'true');
    tag.text = JSON.stringify(schema);
    document.head
      .querySelectorAll('script[data-ivt-breadcrumb-schema="true"]')
      .forEach((existing) => existing.remove());
    document.head.appendChild(tag);

    return () => tag.remove();
  }, [normalizedItems]);

  if (!normalizedItems.length) return null;

  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      <ol className="breadcrumbs-list">
        {normalizedItems.map((item, index) => {
          const isCurrent = index === normalizedItems.length - 1;
          return (
            <li className="breadcrumbs-item" key={`${item.label}-${index}`}>
              {isCurrent || !item.href ? (
                <span aria-current={isCurrent ? 'page' : undefined} className="breadcrumbs-current">
                  {item.label}
                </span>
              ) : (
                <a href={item.href}>{item.label}</a>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
